import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCredits } from '../../contexts/CreditContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';

export default function Topbar({
  onHamburger,
  title,
  sidebarOpen,
  sidebarId,
  showHamburger = false,
}) {
  const { balance } = useCredits();
  const { user } = useAuth();
  const { permissions } = useOrg();

  const creditsText = useMemo(() => {
    if (balance && typeof balance.available === 'number') {
      return balance.available;
    }
    return '...';
  }, [balance]);

  const initials = useMemo(() => getInitials(user?.email), [user?.email]);

  const handleHamburgerClick = (event) => {
    if (!onHamburger) return;
    event.stopPropagation();
    onHamburger();
  };

  const hamburgerClasses = showHamburger
    ? 'inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-200 transition-colors motion-safe:duration-200 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 lg:hidden'
    : 'hidden';

  // Desktop-only left padding to keep header and sidebar both visible (no overlap)
  const desktopOffset = sidebarOpen ? 'lg:pl-72' : 'lg:pl-[72px]';

  return (
    <header className={`sticky top-0 z-30 glass-toolbar border-b border-white/10 supports-[backdrop-filter]:backdrop-blur-2xl ${desktopOffset}`}>
      <div className="flex h-16 items-center gap-3 px-4 lg:px-6">
        <button
          type="button"
          onClick={handleHamburgerClick}
          className={hamburgerClasses}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={showHamburger ? sidebarOpen : undefined}
          aria-controls={showHamburger ? sidebarId : undefined}
        >
          <MenuIcon className="h-5 w-5" />
        </button>

        <div className="text-base font-semibold text-zinc-100 lg:text-lg">{title}</div>

        <div className="ml-auto flex items-center gap-3">
          {(permissions.canAccessAdmin || permissions.role === 'admin') && (
            <Link
              to="/admin"
              className="hidden rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-slate-100 hover:bg-white/10 sm:inline-flex"
            >
              Admin
            </Link>
          )}
          <div className="glass-chip rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-100 shadow-[0_18px_50px_-22px_rgba(246,196,82,0.8)]">
            <span className="uppercase tracking-[0.2em] text-[10px] text-slate-400">Credits</span>
            <span className="ml-2 text-sm font-semibold text-amber-100">{creditsText}</span>
          </div>
          <div className="hidden h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-gradient-to-br from-amber-400/25 via-white/5 to-teal-300/15 text-sm font-semibold text-amber-100 shadow-[0_12px_28px_-16px_rgba(246,196,82,0.7)] sm:flex">
            {initials}
          </div>
        </div>
      </div>
    </header>
  );
}

function getInitials(email) {
  if (!email) return '?';
  const name = email.split('@')[0];
  if (!name) return '?';
  const segments = name.split(/[._-]/).filter(Boolean);
  if (segments.length === 0) {
    return name.slice(0, 2).toUpperCase();
  }
  return segments
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function MenuIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18" strokeLinecap="round" />
      <path d="M3 12h18" strokeLinecap="round" />
      <path d="M3 18h18" strokeLinecap="round" />
    </svg>
  );
}
